/* global globalEvents, SLS_STATE, Spiff */
import { useEffect, useState } from 'preact/hooks'
import Button from 'snippets/button/button'
import { activateSpiffClass, executeTransaction, runSpiff } from './spiff-integration'
import { addCartItem } from 'helpers/cart'
import { eventProps } from 'helpers/global'
import LoaderSpin from 'snippets/loader-spin/loader-spin'

/**
 * @param {
 *  {
 *    spiffProductReference: ProductJSON
 *    shopifyVariantId: number
 *    spiffChildProducts: ProductJSON[]
 *    quantity: number
 *    [setLoading=()=>{}]: Function
 *    addParentVariantToCart: boolean
 *    customParentProperties: { [name: string]: string }
 *    customSpiffProductProperties: : { [name: string]: string }
 *  }
 * }
 * @returns { ReactFrament | void }
 */
async function handlePersonalise({
  spiffProductReference,
  shopifyVariantId,
  spiffChildProducts,
  quantity,
  setLoading = () => {},
  addParentVariantToCart,
  customParentProperties,
  customSpiffProductProperties
}) {
  // console.log('attempting to run personalise')
  const personalisationQueue = [] // : number[]
  const personalisationsOutput = [] // : SpiffTransactionOutput[]
  const childItemsUnpersonalised = spiffChildProducts
    .filter(product => product?.metafields?.spiff?.useUnpersonalisedSleeve)
    .map(product => product.initVariant.sku || product.title)

  if (spiffProductReference) {
    personalisationQueue.push(spiffProductReference.id)
  }

  spiffChildProducts
    .filter(product => product?.metafields?.spiff?.productReference)
    .map(product => product.metafields.spiff.productReference.id)
    .forEach(id => personalisationQueue.push(id))

  // console.log('personalisationQueue', personalisationQueue)

  /**
   *
   * @param {
   *  {
   *    transactionId: string
   *    [name: string]: string
   *  }
   * } outputQueue
   */
  const onAllComplete = (outputQueue) => {
    // console.log('outputQueue', outputQueue)

    // productContains is only defined on the product page, this file is also called on the cart
    const productContains = window.productContains || null

    // add to cart with output queue
    const timestamp = Date.now()
    const url = '/cart/add.js'
    const config = {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      }
    }
    const body = {
      items: []
    }

    // set the base product - if statement is always true unless specifically overridden, when the base product and spiff product are the same.
    if (addParentVariantToCart) {
      // console.log('custom parent props', customParentProperties)
      body.items.push({
        quantity,
        id: shopifyVariantId,
        properties: {
          timestamp,
          ...(productContains ? { ...productContains } : {}), // this is not working atm - needs another look in
          ...(customParentProperties ? { ...customParentProperties } : {})
        }
      })
    }

    if (spiffProductReference) {
      // console.log('customSpiffProductProperties ref', customSpiffProductProperties)
      const primarySpiffProductOutput = outputQueue[0]
      const { transactionId, ...rest } = primarySpiffProductOutput
      body.items.push({
        quantity,
        id: spiffProductReference.variants[0].id,
        properties: {
          timestamp,
          spiffTransactionId: transactionId,
          ...(rest ? { ...rest } : {}), // additional customer customisations
          ...(customSpiffProductProperties ? { ...customSpiffProductProperties } : {})
        }
      })
    }

    body.items = body.items.concat(spiffChildProducts.map((product, index) => {
      // console.log('customSpiffProductProperties concat', customSpiffProductProperties)
      const outputQueueData = outputQueue[spiffProductReference ? index + 1 : index]
      const { transactionId, ...rest } = outputQueueData
      return {
        quantity,
        id: product.initVariant.id,
        properties: {
          timestamp,
          spiffTransactionId: transactionId, // spiff product reference will always be first in the queue if it exists
          ...(rest ? { ...rest } : {}), // customer defined config on the spiff product
          ...(customSpiffProductProperties ? { ...customSpiffProductProperties } : {})
        }
      }
    }))

    config.body = JSON.stringify(body)

    setLoading(true)
    fetch(url, config)
      .then(res => res.json())
      .then(body => {
        console.log('body', body)
        globalEvents.emit(eventProps.cart.add, {})
      })
      .catch(err => {
        console.error('Err adding spiff item to cart', url, config, err)
        // globalEvents.emit(eventProps.cart.) // need an error notification of some sort
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const onCancel = () => {
    console.warn('Spiff customisation cancelled')
  }

  runSpiff({ thisId: personalisationQueue[0], queue: personalisationQueue, outputQueue: personalisationsOutput, onAllComplete, onCancel })

  console.log('spiffProductReference', spiffProductReference, 'spiffChildProducts', spiffChildProducts)
}

/**
 *
 * @param { {
 *  shopifyVariantId: number | null
 *  addParentVariantToCart: boolean
 *  spiffProductReference: ProductJSON | null
 *  spiffUseChildProducts: string[]
 *  buttonClass: string
 *  buttonText: string
 *  isAvailable: boolean
 *  quantity: number
 *  customParentProperties: object
 *  customSpiffProductProperties: object
 * } } param0
 * @returns
 */

function ProductSpiffPersonalise({
  shopifyVariantId, // the parent id to be added to cart
  addParentVariantToCart = true, // special override to prevent the spiffProductReference from being added to cart -- used when the product being added is itself a spiff configurable product (such as the product set up on the cart page)
  spiffProductReference, // should be refactored into the child products - at present, used when the value is singular
  spiffUseChildProducts = [], // used for static hampers which may have many configurables
  buttonClass = 'relative w-full uppercase button-secondary button-large',
  buttonText = 'Add a personalised sleeve',
  isAvailable, // provides an override, else uses the productReference
  quantity = 1,
  customParentProperties = {},
  customSpiffProductProperties = {} // applied to the spiff product, not the parent. Applied to all child products.
}) {
  const [spiffChildProducts, setSpiffChildProducts] = useState([])
  const [loading, setLoading] = useState(true, false)

  if (!shopifyVariantId) {
    console.warn('Spiff button must always have a parent shopifyVariantId')
    return (<></>)
  }

  useEffect(() => {
    if (!spiffUseChildProducts.length) {
      return
    }

    const fetchChildProducts = async () => {
      const productPromises = spiffUseChildProducts.map(handle =>
        fetch(`/products/${handle}?view=ajax`)
          .then(res => res.json())
          .catch(err => {
            console.warn('Could not fetch product', handle, err)
            return null
          })
      )

      const products = await Promise.all(productPromises)
      const filteredProducts = products
        .filter(x => x)
        .filter(x => x.metafields.spiff.productReference || x.metafields.spiff.useUnpersonalisedSleeve)
      setSpiffChildProducts(filteredProducts)
    }

    fetchChildProducts()
      .catch(err => {
        console.error('Could not fetch child products for a hamper needing child product data, spiff', err)
      })
  }, [JSON.stringify(spiffUseChildProducts)])

  if (!spiffProductReference && !spiffUseChildProducts?.length) {
    console.warn('Missing spiffProductReference and have no child products to try', shopifyVariantId, spiffProductReference, spiffUseChildProducts)
    return (<></>)
  }

  // ONLY if isAvailable undefined, use the spiffProductReference's availability as a back up
  isAvailable = isAvailable ?? spiffProductReference ? spiffProductReference.available : false

  // {/* need to figure out a loading state of some description */}
  return (
    <Button
      text={buttonText}
      className={buttonClass}
      onHandle={() => {
        handlePersonalise({
          shopifyVariantId,
          spiffProductReference,
          spiffChildProducts,
          quantity,
          setLoading,
          addParentVariantToCart,
          customParentProperties,
          customSpiffProductProperties
        })
      }}
      disabled={!isAvailable} />
  )
}

export default ProductSpiffPersonalise
